import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CircleUser, Home, LineChart, LogOut, Settings, Shield, Users } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ThemeToggle from './ThemeToggle';
import { fetchSession, hasPermission } from '../lib/session';

export default function AppHeader() {
  const [email, setEmail] = useState<string | null>(null);
  const [canReadUsers, setCanReadUsers] = useState(false);
  const [canReadRoles, setCanReadRoles] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const homeActive = location.pathname === '/home';
  const backtestsActive = location.pathname.startsWith('/backtests');
  const usersActive = location.pathname.startsWith('/admin/users');
  const rolesActive = location.pathname.startsWith('/admin/roles');
  const settingsActive = usersActive || rolesActive;
  const showSettings = canReadUsers || canReadRoles;

  useEffect(() => {
    (async () => {
      const session = await fetchSession();
      if (!session) return;
      setEmail(session.email);
      setCanReadUsers(hasPermission(session, 'users.read'));
      setCanReadRoles(hasPermission(session, 'roles.read'));
    })();
  }, [location.pathname]);

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      navigate('/');
    }
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-card">
      <div className="container mx-auto h-14 flex items-center justify-between px-4">
        <Link to="/home" className="font-semibold">trading-console-app</Link>
        <nav className="flex items-center gap-4">
          <Button asChild variant={homeActive ? 'secondary' : 'ghost'} size="sm">
            <Link to="/home" aria-current={homeActive ? 'page' : undefined}>
              <Home />
              Home
            </Link>
          </Button>
          {email && (
            <Button asChild variant={backtestsActive ? 'secondary' : 'ghost'} size="sm">
              <Link to="/backtests" aria-current={backtestsActive ? 'page' : undefined}>
                <LineChart />
                Backtests
              </Link>
            </Button>
          )}
          {showSettings && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={settingsActive ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-current={settingsActive ? 'page' : undefined}
                >
                  <Settings />
                  Settings
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {canReadUsers && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/users" aria-current={usersActive ? 'page' : undefined}>
                      <Users />
                      Users
                    </Link>
                  </DropdownMenuItem>
                )}
                {canReadRoles && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin/roles" aria-current={rolesActive ? 'page' : undefined}>
                      <Shield />
                      Roles
                    </Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          {email ? (
            <>
              <CircleUser className="w-5 h-5" />
              <span className="text-sm text-muted-foreground hidden sm:inline">{email}</span>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                onClick={onLogout}
                aria-label="Logout"
              >
                <LogOut />
              </Button>
            </>
          ) : (
            <Button asChild size="sm"><Link to="/">Login</Link></Button>
          )}
        </div>
      </div>
    </header>
  );
}
